import {
  PLAN_STRUCTURE_EMPTY,
  PLAN_STRUCTURE_TEMPLATE,
  PLAN_TYPE_KEY_RESIDENT,
  PLAN_TYPE_KEY_TEMPLATE,
} from './case-plan-create.constants';

/**
 * Whether the create form is in template (no-resident) mode.
 * @param {object|null} planType
 * @returns {boolean}
 */
export const isTemplatePlanType = (planType) =>
  planType?.key === PLAN_TYPE_KEY_TEMPLATE;

/**
 * Returns the selected template UUID when structure is "Start from a template".
 * @param {string} planStructure
 * @param {object|null} selectedTemplate
 * @returns {string|undefined}
 */
export const resolveSelectedTemplateId = (planStructure, selectedTemplate) => {
  if (planStructure !== PLAN_STRUCTURE_TEMPLATE) {
    return undefined;
  }

  const templateId = selectedTemplate?.id ?? selectedTemplate?.templateId;
  if (!templateId) {
    return undefined;
  }

  return String(templateId);
};

/**
 * Eligible residents for create (selected, has personId, no existing plan).
 * @param {object[]} selectedResidents
 * @returns {object[]}
 */
export const getEligibleResidentsForCreate = (selectedResidents) =>
  (selectedResidents ?? []).filter(
    (resident) => resident?.personId && !resident.hasPlan,
  );

/**
 * Shared structure + plan-type checks for create submit.
 * @param {object} formState
 * @returns {boolean}
 */
const hasValidStructureSelection = ({
  selectedPlanType,
  planStructure,
  selectedTemplate,
}) => {
  if (!selectedPlanType?.key) {
    return false;
  }

  if (planStructure === PLAN_STRUCTURE_TEMPLATE) {
    return Boolean(resolveSelectedTemplateId(planStructure, selectedTemplate));
  }

  return planStructure === PLAN_STRUCTURE_EMPTY;
};

/**
 * Determines whether the create form has enough data to submit.
 * @param {object} formState
 * @returns {boolean}
 */
export const canSubmitCasePlan = ({
  selectedResidents,
  selectedPlanType,
  planStructure,
  selectedTemplate,
  templateName,
}) => {
  if (
    !hasValidStructureSelection({
      selectedPlanType,
      planStructure,
      selectedTemplate,
    })
  ) {
    return false;
  }

  if (isTemplatePlanType(selectedPlanType)) {
    return String(templateName ?? '').trim().length > 0;
  }

  return getEligibleResidentsForCreate(selectedResidents).length > 0;
};

/**
 * Builds the create-case-plan request payload from form state.
 * Matches POST /api/admin/case-plan body: planType, learnerIds, editorIds, templateId?
 * @param {object} formState
 * @returns {object}
 */
export const buildCreateCasePlanPayload = ({
  selectedResidents,
  selectedPlanType,
  planStructure,
  selectedTemplate,
  editors,
}) => {
  const isTemplate = isTemplatePlanType(selectedPlanType);

  const learnerIds = isTemplate
    ? []
    : getEligibleResidentsForCreate(selectedResidents)
        .map((resident) => Number(resident.personId))
        .filter((personId) => Number.isFinite(personId) && personId > 0);

  const editorIds = (editors ?? [])
    .map((editor) => Number(editor.personId))
    .filter((personId) => Number.isFinite(personId) && personId > 0);

  const payload = {
    planType: selectedPlanType?.key ?? PLAN_TYPE_KEY_RESIDENT,
    learnerIds,
    editorIds,
  };

  const templateId = resolveSelectedTemplateId(planStructure, selectedTemplate);
  if (templateId) {
    payload.templateId = templateId;
  }

  return payload;
};
